import { Vue, Component, Ref, Prop, Provide, Emit } from 'vue-property-decorator'
import {
    LaboratoryProcessConfigurationCreateRequest,
    LaboratoryProcessConfigurationPatchInBatchRequest,
    LaboratoryProcessConfigurationResponse
} from '@/modules/configurationModule/detail/local-laboratory-module/laboratory-process/services/laboratory-process/laboratory-process.dto'
import {
    initialLaboratoryProcessModalState,
    LaboratoryProcessModalState
} from '@/modules/configurationModule/detail/local-laboratory-module/laboratory-process/components/modal/laboratory-process.model'
import { OperationResponse } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/operation/operation.dto'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { operationService } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/operation/operation.service'
import ConfigurationFieldsTranslations
    from '@/modules/configurationModule/shared/components/configuration-fields-translations/configuration-fields-translations.vue'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { laboratoryProcessService } from '@/modules/configurationModule/detail/local-laboratory-module/laboratory-process/services/laboratory-process/laboratory-process.service'
import { TranslationRequest, TranslationResponse } from '@/api/services/common.dto'
import { StrictNullable } from '@/shared/types/app.types'
import { BModal } from 'bootstrap-vue'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationFieldsTranslations
    }
})
export default class LaboratoryProcessModal extends Vue {
    @Ref() readonly laboratoryProcessModal!: BModal

    @Provide() externalValidator = this.$validator

    @Prop(String) readonly actionTitle!: string

    @Emit()
    reloadData(): void {}

    state: LaboratoryProcessModalState
    constructor() {
        super()
        this.state = initialLaboratoryProcessModalState()
    }

    get updateMode(): boolean {
        return !!this.state.id
    }

    get modelValidated(): boolean {
        const hasErrors: boolean = this.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        const validOrder: boolean = this.state.order >= 0 && this.state.order !== ''
        const hasDefaultTranslation: boolean = !!this.state.nameTranslations?.[0]?.value?.length
        return !hasErrors && isDirty && validOrder && hasDefaultTranslation
    }

    show(laboratoryProcess: StrictNullable<LaboratoryProcessConfigurationResponse>): void {
        this.state = initialLaboratoryProcessModalState()
        if (laboratoryProcess) {
            this.state = {
                operation: laboratoryProcess.operation,
                order: laboratoryProcess.order,
                nameTranslations: laboratoryProcess.nameTranslations,
                id: laboratoryProcess.id,
                laboratoryId: laboratoryProcess.laboratoryId
            }
        }
        this.laboratoryProcessModal.show()
    }

    async getOperations(query: string): Promise<OperationResponse[]> {
        const filterModel = {
            code: GridUtils.getTextFilterModel('contains', query)
        }

        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(operationService.getAllTaskSectionOperations(filterModel, params))
        if (response) {
            return response.content
        }
        return []
    }

    hide(): void {
        this.laboratoryProcessModal.hide()
    }

    mapTranslationsToRequest(translations: TranslationResponse[]): TranslationRequest[] {
        return translations.map(translation => {
            return {
                languageId: translation.language.id,
                value: translation.value
            }
        })
    }

    async update(): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const batchRequests: LaboratoryProcessConfigurationPatchInBatchRequest[] = [{
            id: this.state.id!,
            nameTranslations: this.mapTranslationsToRequest(this.state.nameTranslations),
            order: this.state.order as number
        }]
        const response = await this.$_toCallUnique(laboratoryProcessService.patch(this.state.laboratoryId, batchRequests))
        if (response) {
            const elements = batchRequests.length - response.length
            if (elements > 0) {
                const choice = elements > 1 ? 2 : 1
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
            }
            this.hide()
            this.reloadData()
            return response
        }
        return null
    }

    async create(): Promise<StrictNullable<LaboratoryProcessConfigurationResponse>> {
        const createRequest: LaboratoryProcessConfigurationCreateRequest = {
            operationId: this.state.operation!.id,
            nameTranslations: this.mapTranslationsToRequest(this.state.nameTranslations),
            order: this.state.order as number
        }
        const response = await this.$_toCallUnique(laboratoryProcessService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.hide()
            this.reloadData()
            return response
        }
        return null
    }

    async doAction(): Promise<void> {
        if (this.updateMode) {
            await this.update()
        } else {
            await this.create()
        }
    }
}
